import { createContext, useContext, useMemo, type ReactNode } from "react"
import { keepPreviousData } from "@tanstack/react-query"

import { opsNeedRun } from "~/utils/allocationPackageOps"
import { api } from "~/utils/api"
import { pluralize } from "~/utils/formatting"
import { showErrorToast } from "~/utils/toaster"
import { DraftCommandbar, type SaveCallbacks } from "../DraftCommandbar"
import { withSubfolders } from "../packageFolders"
import {
  toPackageFolder,
  toRangePackage,
  useAllocationPackageFolders,
  useAllocationPackages,
  useAllocationStores,
} from "../useAllocationData"
import { packageDraftChanges } from "./drafts/packageDraftChanges"
import { storesToggleOp, withStoreOps } from "./drafts/packageDraftState"
import { PackageDraftImpact } from "./PackageDraftImpact"
import { noteRunAfterCurrent, useSetupDrafts } from "./setupDrafts"

const NO_STORES: ReadonlySet<string> = new Set()

const CHECKING_REASON = "Checking your last change…"

/**
 * The package draft as a page shows it: the packages and folders as
 * publishing would leave them, what changed, and the calls that stage,
 * discard or publish. Computed once per page by PackageDraftProvider.
 */
function usePackageDraftValue() {
  const { packageStaging: staging, publishPackageDraft } = useSetupDrafts()
  const { ops, deletions, setDeletions, waiting, publishOps } = staging
  const { packages: publishedPackages, isLoading: packagesLoading } = useAllocationPackages()
  const { folders: publishedFolders, isLoading: foldersLoading } = useAllocationPackageFolders()
  const previewQuery = api.allocation.previewPackageDraft.useQuery(
    { ops },
    { enabled: ops.length > 0, placeholderData: keepPreviousData, staleTime: 60_000 },
  )
  const { stores, isLoading: storesLoading } = useAllocationStores()

  const published = useMemo(
    () => ({ packages: publishedPackages, folders: publishedFolders }),
    [publishedPackages, publishedFolders],
  )
  const previewData = ops.length > 0 && previewQuery.data?.ok ? previewQuery.data : null
  const preview = useMemo(
    () =>
      previewData
        ? {
            packages: previewData.packages.map(toRangePackage),
            folders: previewData.folders.map(toPackageFolder),
          }
        : published,
    [previewData, published],
  )
  // Someone else's publish can make the draft fail its check: show the published tree and why.
  const previewError =
    ops.length > 0 && previewQuery.data?.ok === false ? previewQuery.data.error : undefined
  const tree = useMemo(() => {
    if (waiting.length === 0) return preview
    const waitingByPackage = Map.groupBy(waiting, (op) => op.packageId)
    return {
      ...preview,
      packages: preview.packages.map((pkg) => {
        const own = waitingByPackage.get(pkg.id)
        return own ? { ...pkg, storeIds: withStoreOps(pkg.storeIds, own) } : pkg
      }),
    }
  }, [preview, waiting])

  const diff = useMemo(() => {
    const base = packageDraftChanges(published, preview, deletions)
    if (!previewError) return base
    const stale = {
      key: "draft-stale",
      label: `Draft no longer applies (${pluralize(ops.length, "change")})`,
      before: "",
      after: previewError,
    }
    return { ...base, changes: [...base.changes, stale] }
  }, [published, preview, deletions, previewError, ops.length])
  const savedStoreIds = useMemo(
    () => new Map(published.packages.map((pkg) => [pkg.id, new Set(pkg.storeIds)])),
    [published.packages],
  )
  const storeIds = useMemo(
    () => new Map(tree.packages.map((pkg) => [pkg.id, new Set(pkg.storeIds)])),
    [tree.packages],
  )
  const deletingFolderIds = useMemo(
    () => withSubfolders(tree.folders, deletions.folderIds),
    [tree.folders, deletions.folderIds],
  )
  const deletingPackageIds = useMemo(
    () =>
      new Set([
        ...deletions.packageIds,
        ...tree.packages
          .filter((pkg) => pkg.folderId !== null && deletingFolderIds.has(pkg.folderId))
          .map((pkg) => pkg.id),
      ]),
    [tree.packages, deletions.packageIds, deletingFolderIds],
  )
  const saving = publishPackageDraft.isPending
  const storeIdsOf = (packageId: string) => storeIds.get(packageId) ?? NO_STORES

  return {
    packages: tree.packages,
    folders: tree.folders,
    packagesLoading: packagesLoading || foldersLoading,
    stores,
    storesLoading,
    storeIdsOf,
    /** Whether the published list has the store. */
    isSaved: (packageId: string, storeId: string) =>
      (savedStoreIds.get(packageId) ?? NO_STORES).has(storeId),
    /** Stages ops; they join the draft once the preview accepts them, else their reason shows. */
    stage: (...args: Parameters<typeof staging.stage>) => {
      if (!saving) staging.stage(...args)
    },
    /** True while staged ops wait for their preview. */
    staging: staging.staging,
    toggle: (packageId: string, storeIds: string[], assign: boolean) => {
      const op = storesToggleOp(packageId, storeIdsOf(packageId), storeIds, assign)
      if (op && !saving) staging.stage(op)
    },
    isPackageUnpublished: (packageId: string) => diff.packageIds.has(packageId),
    isFolderUnpublished: (folderId: string) => diff.folderIds.has(folderId),
    isDeleting: (packageId: string) => deletingPackageIds.has(packageId),
    isFolderDeleting: (folderId: string) => deletingFolderIds.has(folderId),
    markForDeletion: (target: { packageId?: string; folderId?: string }) =>
      !saving &&
      setDeletions((prev) => ({
        packageIds:
          target.packageId && !prev.packageIds.includes(target.packageId)
            ? [...prev.packageIds, target.packageId]
            : prev.packageIds,
        folderIds:
          target.folderId && !prev.folderIds.includes(target.folderId)
            ? [...prev.folderIds, target.folderId]
            : prev.folderIds,
      })),
    unmarkForDeletion: (target: { packageId?: string; folderId?: string }) =>
      !saving &&
      setDeletions((prev) => ({
        packageIds: prev.packageIds.filter((id) => id !== target.packageId),
        folderIds: prev.folderIds.filter((id) => id !== target.folderId),
      })),
    changes: diff.changes,
    /** Every staged change as Publish sends it. */
    publishOps,
    /** Why Publish can't run yet. */
    publishDisabledReason: previewError ?? (staging.staging ? CHECKING_REASON : undefined),
    saving,
    discard: staging.reset,
    save: (callbacks?: SaveCallbacks) =>
      publishPackageDraft.mutate(
        { ops: publishOps },
        {
          onSuccess: (result) => {
            staging.reset()
            noteRunAfterCurrent(result.run)
            callbacks?.onSaved?.()
          },
          onError: (error) => {
            showErrorToast(error)
            callbacks?.onFailed?.()
          },
        },
      ),
  }
}

export type PackageDraft = ReturnType<typeof usePackageDraftValue>

const PackageDraftContext = createContext<PackageDraft | null>(null)

/** Wraps a page that shows or edits the package draft (Coverage, Packages). */
export function PackageDraftProvider({ children }: { children: ReactNode }) {
  const value = usePackageDraftValue()
  return <PackageDraftContext.Provider value={value}>{children}</PackageDraftContext.Provider>
}

export function usePackageDraft(): PackageDraft {
  const value = useContext(PackageDraftContext)
  if (value === null) throw new Error("usePackageDraft needs PackageDraftProvider")
  return value
}

/** The bottom bar for the package draft, on Coverage and Packages. */
export function PackageDraftCommandbar() {
  const draft = usePackageDraft()
  return (
    <DraftCommandbar
      changes={draft.changes}
      saving={draft.saving}
      saveDisabledReason={draft.publishDisabledReason}
      onDiscard={draft.discard}
      onSave={draft.save}
      aside={opsNeedRun(draft.publishOps) && <PackageDraftImpact ops={draft.publishOps} />}
    />
  )
}
